import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'ui-components';
import { CreatorPlatformsType } from '~/clientApi';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { getEventStreamContent } from '~/core/utils/eventStreamContent';
import {
  SmartProfileInformationResponse,
  areAllFieldsDone,
  useGetSmartProfileInformationFromRequestId,
} from '~/hooks/api/useGetSmartProfileInformation';
import { useSubmitSmartProfileInformation } from '~/hooks/api/useSubmitSmartProfileInformation';

export const useGetSmartProfile = (
  requestId?: string,
  toggleSmartProfileEnabled: boolean = true,
  platforms: CreatorPlatformsType = {},
) => {
  const snackbar = useSnackbar();
  const [hasRequested, setHasRequested] = useState(false);

  const scpRequestInfo = useSubmitSmartProfileInformation();
  const [reqId, setReqId] = useState<undefined | string>(undefined);

  // whalar-mode flow also make use of this hook
  // please be aware in case any changes are done
  const { data: requestData } = useGetSmartProfileInformationFromRequestId(
    toggleSmartProfileEnabled ? requestId : undefined,
    {
      enabled: !!requestId && toggleSmartProfileEnabled,
      onError: () => {
        snackbar.error({
          ...SNACKBAR_ERROR_GENERIC_OPTIONS,
          description: `Failed to fetch creator profile}`,
        });
      },
    },
  );

  const {
    data: creatorData,
    isFetching,
    isError,
    error: byCreatorError,
  } = useGetSmartProfileInformationFromRequestId(reqId, {
    enabled: !!reqId && hasRequested, // we need this enabled after consumer calls refetch so that refetch mechanism can work.
    onError: () => {
      snackbar.error({
        ...SNACKBAR_ERROR_GENERIC_OPTIONS,
        description: `Failed to fetch creator profile`,
      });
    },
  });

  const data = requestId ? requestData : creatorData;

  useEffect(() => {
    if (isError) {
      setHasRequested(false);
    }
  }, [isError]);

  const fetchCreatorProfile = useCallback(async () => {
    if (!hasRequested) {
      setHasRequested(true);

      const accounts = Object.entries(platforms)
        .filter(
          ([platform, accountInfo]) =>
            accountInfo.handle && platform !== 'snapchat',
        )
        .map(([platform, accountInfo]) => ({
          platform,
          handle: accountInfo.handle as string,
        }));

      if (accounts.length > 0) {
        scpRequestInfo.mutate(
          { accounts },
          {
            onSuccess: async (response) => {
              setReqId(response.request_id);
            },
            onError: () => {
              snackbar.error({
                ...SNACKBAR_ERROR_GENERIC_OPTIONS,
                description:
                  'Error getting requestId for smart creator profile',
              });
              setHasRequested(false);
            },
          },
        );
      } else {
        snackbar.error({
          description: 'No valid platform accounts found for this creator',
        });
        setHasRequested(false);
        throw new Error('No valid platform accounts found for this creator');
      }
    }
    return data;
  }, [hasRequested, data, platforms, scpRequestInfo, snackbar]);

  const isSuccess = useMemo(
    () => (data ? areAllFieldsDone(data) : false),
    [data],
  );

  return {
    data,
    fetchCreatorProfile,
    hasRequested,
    isSuccess,
    isFetching: isFetching || (hasRequested && !isError && !isSuccess), // we have to do this because when enabled is false(hasRequested is false) the isLoading is not set to false by useQuery so we have to check if the data is done and if the isError is false
    error: byCreatorError,
  };
};

// FOAM-11420, This is just a proof of concept to showcase how it would work with a streaming endpoint rather than polling until completion.
export const useGetSmartProfileStreaming = (requestId?: string) => {
  const query = useQuery({
    queryKey: ['talent-profile-smart_profile', { aiRequestId: requestId }],
    queryFn: ({ queryKey }) =>
      getEventStreamContent<SmartProfileInformationResponse>(
        queryKey,
        `https://sse-poc.onrender.com/items/${requestId}`,
      ),
    staleTime: Infinity,
    enabled: false,
  });

  const fetchCreatorProfile = useCallback(() => {
    query.refetch();
  }, [query]);

  const isSuccess = useMemo(
    () => (query.data ? areAllFieldsDone(query.data) : false),
    [query.data],
  );

  return {
    ...query,
    fetchCreatorProfile,
    isSuccess,
  };
};
